import { Key } from 'react'
import Styles from './ChatHistory.module.scss'
import { useInfiniteThreads } from 'hooks/queries/chat/useInfiniteThreads'
import { collectResults } from 'utils/query'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { Clock1, Plus } from 'blaze-ui/icons'
import { Menu } from 'components/Menu/Menu'
import { Section } from '@react-stately/collections'
import { Item } from 'components/Menu/MenuItem'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { groupBy } from 'lodash'
import { Skeleton } from 'components/Skeleton/Skeleton'

interface Props {
  fileId?: string
  selectedThreadId?: number
  setSelectedThreadId: (threadId?: number) => void
}

const LoadingItem = () => {
  return (
    <div className={Styles.loadingItem}>
      <Skeleton
        active
        title={{ width: '100%', style: { height: '20px', borderRadius: '4px', margin: 0, padding: 0 } }}
        paragraph={false}
        avatar={false}
      />
    </div>
  )
}

const ChatHistory = ({ fileId, selectedThreadId, setSelectedThreadId }: Props) => {
  const threadsQuery = useInfiniteThreads({ fileId })
  const threads = collectResults(threadsQuery, (data) => data.threads)
  const { hasNextPage, fetchNextPage } = threadsQuery
  const selectedThreadKey = !!selectedThreadId ? [selectedThreadId.toString()] : []

  const groupedThreads = groupBy(threads, (thread) => {
    if (thread.id === selectedThreadId) return 'Current chat'
    const daysAgo = Math.floor((new Date().getTime() - new Date(thread.updated_at).getTime()) / (1000 * 60 * 60 * 24))
    if (daysAgo < 7) return 'Last 7 days'
    if (daysAgo < 14) return 'Last 14 days'
    return 'Older'
  })

  const orderedGroups = ['Current chat', 'Last 7 days', 'Last 14 days', 'Older']
  const sortedGroupedThreads = Object.fromEntries(
    orderedGroups.filter((group) => groupedThreads[group]?.length > 0).map((group) => [group, groupedThreads[group]]),
  )

  const handleAction = (key: Key, close: () => void) => {
    if (key === 'newChat') {
      setSelectedThreadId(undefined)
    } else {
      setSelectedThreadId(parseInt(key as string))
    }

    close()
  }

  if (threads.length === 0) return null

  return (
    <DropdownTrigger placement="bottom-start" offsetOptions={{ mainAxis: 7 }} maxHeight={300}>
      <IconButton rounded variant="editor" icon={Clock1} aria-label="Chat history" />
      {(close) => (
        <>
          <Menu
            onAction={(key) => {
              handleAction(key, close)
            }}
            aria-label="Chat history"
            selectionMode="single"
            itemClassName={Styles.item}
            selectedKeys={selectedThreadKey}
          >
            <Item key="newChat" textValue="New Chat" frontIcon={Plus}>
              New Chat
            </Item>
            {Object.entries(sortedGroupedThreads).map(([group, threads]) => (
              <Section key={group} title={group}>
                {threads.map((thread) => (
                  <Item key={thread.id} textValue={thread.name ?? 'Untitled Chat'}>
                    {thread.name}
                  </Item>
                ))}
              </Section>
            ))}
          </Menu>
          {hasNextPage && (
            <InfiniteScroll hasMore={hasNextPage} loadMore={fetchNextPage}>
              <LoadingItem />
            </InfiniteScroll>
          )}
          {hasNextPage && Array.from({ length: 2 }).map((_, i) => <LoadingItem key={i} />)}
        </>
      )}
    </DropdownTrigger>
  )
}

export { ChatHistory }
